import { FC, useEffect, useState } from "react";
import { fetchAsync } from "../../services/ApiHandler";
import { removeToken } from "../../services/CookieHandler";
import { GenerationRequest } from "../../model/GenerationRequest";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { ComboboxItem, Loader, Select } from "@mantine/core";
import { Helmet } from "react-helmet";

export const GenerationHistoryPage: FC = () => {
  const [requestList, setRequestList] = useState<GenerationRequest[] | undefined>(undefined);
  const [selectedRequest, setSelectedRequest] = useState<GenerationRequest | undefined>(undefined);
  const [hasNextData, setHasNextData] = useState(true);
  const [orderValue, setOrderValue] = useState<ComboboxItem>({ value: 'desc', label: '新しい順' });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if(version > 0){
    fetchGeneratedList();
    }
  }, [version]);

  async function fetchGeneratedList(){
    var param = { count: 50, page: page, asc: orderValue!.value === 'asc', containsNsfw: false, containsError: false };
    const list = await fetchAsync<GenerationRequest[]>('Generation/GetGeneratedImages', param);
  
    // 関数型アップデートを使用してステート更新
    setRequestList((prevRequestList) => {
      const newList = prevRequestList?.concat(list); // 直前のリストと新しいリストを結合
      console.log(newList?.length); // リストの長さを確認
      return newList;
    });

    if(list.length === 0) {
      setHasNextData(false); // 次のデータがない場合の処理
    }
  };

  async function getNextData(){
    if(isProcessing) {
      return;
    }

    if(hasNextData === false){
      return;
    }

    setPage(prevPage => prevPage + 1);
    setVersion(prevVersion => prevVersion + 1);
  }

  useEffect(() => {
    // fetchGeneratedList();
    listenScrollEvent();
  }, [])

  useEffect(() => {
    setRequestList([]);
    setPage(0);
    setVersion(prevVersion => prevVersion + 1);
    setHasNextData(true);

  }, [orderValue])

  function listenScrollEvent() {
    window.addEventListener('scroll', function() {
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const documentHeight = document.body.scrollHeight;
      if (windowHeight + scrollTop >= documentHeight - 20) {
        console.log("画面の下までスクロールしました");
        getNextData();
      }
    });
  }

  function buildDialog(){
    return <>
    <div id="processing-dialog" className="dialog-overlay">
      <div className='dialog text-start max-h-[80vh] overflow-scroll' style={{ width: "1080px", paddingTop: '0px' }}>
        <div className="pb-10 pt-4 bg-white" style={{ position: 'sticky', top: '0px'}}>
          <div className="mb-2 text-lg flex justify-end absolute w-full">
            <img onClick={(e) => setSelectedRequest(undefined)} src="/images/icon-close.svg" className="clickable w-[32px]"></img>
          </div>
        </div>
        <div className="flex">
          <div className="w-[50%] flex items-center min-h-[500px] justify-center">
            <img className="w-[500px]"  src={`${process.env.REACT_APP_IMAGE_URL}/${selectedRequest!.requestId}`} />
          </div>
          <div className="w-[50%] ml-4">
            <div className="text-sm">モデル</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.tasteName }
            </div>
            <div className="text-sm mt-2">シード値</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.seed }
            </div>
            <div className="text-sm mt-2">ステップ数</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.inferenceSteps }
            </div>
            <div className="text-sm mt-2">スケール</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.guidanceScale }
            </div>
            <div className="text-sm mt-2">アスペクト比</div>
            <div className="text-sm border py-1 px-2 rounded">
              { `${selectedRequest!.aspectRatio} (${selectedRequest!.width}px x ${selectedRequest!.height}px)` }
            </div>
            <div className="text-sm mr-4 mt-4">プロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
              { `${selectedRequest!.prompt} ${selectedRequest!.additionalPrompt}` }
            </div>
            <>
            <div className="text-sm mt-4">ネガティブプロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
            { `${selectedRequest!.negativePrompt} ${selectedRequest!.additionalNegativePrompt}` }
            </div>
            </>
          </div>
        </div>
      </div>
    </div>
  </>;
  }

  return <>
    <Helmet>
      <title>生成履歴 - Drop AI</title>
      <meta name="description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
      <meta property="og:url" content="https://drop4i.com/generation-history" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="生成履歴 - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
    </Helmet>
    { selectedRequest &&
      buildDialog()
    }
    <div className="max-h-[calc(100vh-76px)] overflow-scroll">
      <div className="flex items-center mb-1 justify-between sm:justify-start">
        <div className="text-[30px] ml-3 sm:ml-0">生成履歴</div>
        <Select
          className="ml-4 mr-2 sm:mr-0"
          data={[
            { value: 'asc', label: '古い順' },
            { value: 'desc', label: '新しい順' }
          ]}
          value={orderValue ? orderValue.value : null}
          onChange={(_value, option) => setOrderValue(option)}
        />
      </div>
      { requestList === undefined &&
        <div className="min-h-[calc(100vh-76px)] h-[900px]"> 
        </div>
      }
      { requestList !== undefined &&
      <div className="cards min-h-[calc(100vh-76px)] mt-2 m-auto max-f-full sm:max-w-[1200px] w-full sm:w-[1200px] bg-white inline-flex flex-wrap justify-around sm:justify-start"> 
        { requestList?.map((e) => (
          <div className="card">
            <img onClick={() => setSelectedRequest(e)} className="clickable max-w-[220px] sm:max-w-[240px]" style={{ borderRadius: "8px"}} src={`${process.env.REACT_APP_IMAGE_URL}/${e.requestId}_mid`} />
          </div>
        ))}
        </div>
      }
    </div>
  </>;
}
  